import type { ProjectOverride } from "@/lib/board-projects";
import { memo, useCallback, useMemo, useRef } from "react";
import { AlertTriangle, AtSign, Ban, CalendarClock, Check, Loader2, MessageCircleQuestion, MessageSquare, Reply } from "lucide-react";
import { Avatar } from "@/components/ui/misc";
import { avatarOf } from "@/lib/avatars";
import { useForeignPresence } from "@/hooks/use-foreign-presence";
import { Hint } from "@/components/ui/tooltip";
import { formatDue } from "@/lib/format";
import { previewText } from "@/lib/preview-text";
import { cn } from "@/lib/utils";
import { ProjectChip } from "@/components/project-select";
import { useWaiting } from "@/lib/waiting-context";
import { reasonKinds, WAITING_REASON_LABELS } from "@/lib/waiting";
import { priorityColor, type BoardResponseCount, type ColumnKind, type Project, type Task, type User } from "@/lib/types";

/**
 * Native HTML5 drag only where there is a mouse or trackpad. It never fires for a
 * finger — touch drags go through `useTouchDrag` instead — and on an iPad with
 * both, leaving it on would start two drags for one gesture.
 */
const FINE_POINTER = typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches;

const ASSIGNEE_TINT: Record<string, string> = { me: "var(--kind-active)", claude: "var(--primary)" };

export interface TaskCardProps {
  task: Task;
  columnKind: ColumnKind;
  users: User[];
  now: number;
  /** Unresolved `@claude` requests in this card's thread. */
  openMentions?: number;
  /** Draft replies held against this card, and how many are the user's to send. */
  replies?: BoardResponseCount;
  /** Set only when this card points somewhere other than its board's default project. */
  projectOverride?: ProjectOverride;
  dragging: boolean;
  onOpen: () => void;
  /** Omitted where there is nowhere to drop a card, such as the list layout. */
  onDragStart?: (event: React.DragEvent) => void;
  onDragEnd?: () => void;
  /** A control at the end of the footer row, such as the list layout's move menu. */
  action?: React.ReactNode;
}

/**
 * A card carries the four things that decide whether you act on it — priority,
 * owner, deadline, and whether it is stuck — without needing to be opened. The
 * left rail is priority-coloured so a column scans by urgency at a glance.
 */
export function TaskCard(props: TaskCardProps) {
  // A board draws a card per task and re-renders whenever anything on it changes, so
  // the card itself is memoised. Its callbacks are made inline by the column; these
  // stable ones call whatever the latest render passed, so a card that skipped a
  // render never acts on a stale closure.
  const latest = useRef(props);
  latest.current = props;
  const onOpen = useCallback(() => latest.current.onOpen(), []);
  const onDragStart = useCallback((event: React.DragEvent) => latest.current.onDragStart?.(event), []);
  const onDragEnd = useCallback(() => latest.current.onDragEnd?.(), []);
  return (
    <TaskCardBody
      {...props}
      onOpen={onOpen}
      onDragStart={props.onDragStart ? onDragStart : undefined}
      onDragEnd={props.onDragEnd ? onDragEnd : undefined}
    />
  );
}

const TaskCardBody = memo(function TaskCardBody({
  task,
  columnKind,
  users,
  now,
  openMentions = 0,
  replies,
  projectOverride,
  dragging,
  onOpen,
  onDragStart,
  onDragEnd,
  action,
}: TaskCardProps) {
  const assignee = users.find((user) => user.id === task.assigneeId);
  const overdue = columnKind !== "done" && task.dueAt !== null && new Date(task.dueAt).getTime() < now;
  const dueSoon =
    !overdue && columnKind !== "done" && task.dueAt !== null && new Date(task.dueAt).getTime() - now < 24 * 3_600_000;
  const done = columnKind === "done";
  // Read from the app-wide list rather than passed down: every place a card is
  // drawn (columns, the stacked list) gets it without another prop to thread.
  const waiting = useWaiting().byTask.get(task.id);
  // The same, for a foreign agent's mark: a string-keyed select, so it re-renders only when one's presence moves.
  const foreign = useForeignPresence();
  // A snippet, so plain text: a description is often an agent's markdown, and
  // two clamped lines of `## Why` and `- [ ]` say nothing about the card.
  const snippet = useMemo(() => (task.description ? previewText(task.description) : ""), [task.description]);

  return (
    <article
      data-task-id={task.id}
      draggable={FINE_POINTER && onDragStart !== undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`${task.title} — ${task.priority} priority${waiting ? " — needs your answer" : ""}`}
      className={cn(
        "group relative touch-manipulation select-none overflow-hidden rounded-md border border-border/80 bg-card p-2.5 pl-3 text-left [-webkit-touch-callout:none]",
        "shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-150",
        "hover:-translate-y-px hover:border-ring/45 hover:shadow-[0_6px_16px_-8px_rgba(0,0,0,0.35)]",
        onDragStart ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        dragging && "opacity-40 rotate-[0.6deg] scale-[0.99]",
        done && "opacity-70 hover:opacity-100",
        // A ring and a glow in its own hue, so it cannot be read as priority
        // (the rail) or overdue (red text): somebody is waiting on the user.
        waiting &&
          "border-waiting/70 bg-[color-mix(in_oklab,var(--waiting)_6%,var(--card))] opacity-100 shadow-[0_0_0_1px_var(--waiting),0_0_18px_-6px_var(--waiting)] hover:border-waiting hover:shadow-[0_0_0_1px_var(--waiting),0_8px_22px_-8px_var(--waiting)]",
      )}
    >
      {/* Priority rail */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px] rounded-l-md transition-[width] group-hover:w-1"
        style={{ backgroundColor: priorityColor(task.priority) }}
      />

      {waiting ? (
        <Hint label={`Waiting on you: ${reasonKinds(waiting).map((kind) => WAITING_REASON_LABELS[kind].toLowerCase()).join(", ")}`}>
          <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-waiting px-1.5 py-px text-[10px] font-semibold text-waiting-foreground">
            <MessageCircleQuestion className="size-3" />
            Needs your answer
          </span>
        </Hint>
      ) : null}

      <div className="flex items-start gap-2">
        <p
          className={cn(
            "min-w-0 flex-1 text-[13px] font-medium leading-snug text-card-foreground [overflow-wrap:anywhere]",
            done && "line-through decoration-muted-foreground/60",
          )}
        >
          {task.title}
        </p>
        {assignee ? (
          <Avatar {...avatarOf(assignee)} tint={ASSIGNEE_TINT[assignee.id]} size="sm" foreign={foreign.get(assignee.id)} />
        ) : (
          <Hint label="Unassigned">
            <span className="mt-0.5 size-5 shrink-0 rounded-full border border-dashed border-border" />
          </Hint>
        )}
      </div>

      {snippet ? <p className="mt-1 line-clamp-2 text-[11.5px] leading-snug text-muted-foreground [overflow-wrap:anywhere]">{snippet}</p> : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10.5px] text-muted-foreground">
        <span
          className="font-medium uppercase tracking-wide"
          style={{ color: priorityColor(task.priority) }}
        >
          {task.priority}
        </span>

        {/* An unanswered ask outranks the rest of this row: somebody is waiting. */}
        {openMentions > 0 ? (
          <Hint label={`${openMentions} @agent request${openMentions > 1 ? "s" : ""} waiting for a reply`}>
            <span
              className="inline-flex items-center gap-1 rounded-full px-1.5 py-px font-medium ring-1 ring-inset"
              style={{
                color: "var(--primary)",
                backgroundColor: "color-mix(in oklab, var(--primary) 12%, transparent)",
                // @ts-expect-error CSS custom property for the ring color
                "--tw-ring-color": "color-mix(in oklab, var(--primary) 30%, transparent)",
              }}
            >
              <AtSign className="size-2.5" />
              {openMentions > 1 ? `${openMentions} asks` : "asked"}
            </span>
          </Hint>
        ) : null}

        {/* A drafted reply is the other half of a card that came out of somebody's
            inbox: the chip says one is waiting so it can be found without opening
            every card, and highlights only when it is the user's to send. */}
        {replies && replies.open > 0 ? (
          <Hint
            label={
              replies.working
                ? "Claude is rewriting one of this card's draft replies"
                : replies.dueNow > 0
                  ? `${replies.dueNow} draft repl${replies.dueNow > 1 ? "ies" : "y"} ready for you to send`
                  : `${replies.open} draft repl${replies.open > 1 ? "ies" : "y"}, for when this card is done`
            }
          >
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-1.5 py-px ring-1 ring-inset",
                replies.dueNow > 0 && "font-medium",
              )}
              style={{
                color: replies.dueNow > 0 ? "var(--kind-active)" : "var(--muted-foreground)",
                backgroundColor:
                  replies.dueNow > 0 ? "color-mix(in oklab, var(--kind-active) 12%, transparent)" : "transparent",
                // @ts-expect-error CSS custom property for the ring color
                "--tw-ring-color": `color-mix(in oklab, ${
                  replies.dueNow > 0 ? "var(--kind-active)" : "var(--border)"
                } 30%, transparent)`,
              }}
            >
              {replies.working ? <Loader2 className="size-2.5 animate-spin" /> : <Reply className="size-2.5" />}
              {replies.dueNow > 0 ? `${replies.dueNow} to send` : replies.open}
            </span>
          </Hint>
        ) : null}

        {/* Only ever an override: it is here to say "this one is somewhere else",
            which is the only thing about a project a column scan needs to know. */}
        {projectOverride ? <ProjectChip project={projectOverride.project} via={projectOverride.foreign ? "task" : undefined} /> : null}

        {task.dueAt ? (
          <span
            className={cn("inline-flex items-center gap-1", overdue && "font-semibold text-destructive")}
            style={dueSoon ? { color: "var(--kind-review)" } : undefined}
          >
            {overdue ? <AlertTriangle className="size-3" /> : <CalendarClock className="size-3" />}
            {overdue ? "overdue" : formatDue(task.dueAt, now)}
          </span>
        ) : null}

        {task.blockedReason ? (
          <Hint label={task.blockedReason}>
            <span className="inline-flex max-w-32 items-center gap-1 truncate" style={{ color: "var(--kind-blocked)" }}>
              <Ban className="size-3 shrink-0" />
              {task.blockedReason}
            </span>
          </Hint>
        ) : null}

        {done && task.completedAt ? (
          <span className="inline-flex items-center gap-1" style={{ color: "var(--kind-done)" }}>
            <Check className="size-3" /> done
          </span>
        ) : null}

        <span className="ml-auto font-mono text-[9.5px] opacity-0 transition-opacity group-hover:opacity-50">
          {task.id}
        </span>
        {action ? (
          // Stops here so pressing the control (or an item of a menu it opens, whose
          // events bubble through the portal) does not also open the card.
          <span className="-my-1 -mr-1 flex" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            {action}
          </span>
        ) : null}
      </div>
    </article>
  );
});

export { MessageSquare };
